import { ActionButton, IIconProps, IconButton } from '@fluentui/react';
import { Cell, Column, HeaderGroup, Row, TableInstance, useSortBy, useTable } from 'react-table';
import { EventType, LogComponent, LogElement, LogTarget } from "../../../models/LogModel";
import { EventTypeEnum, OpenFeedbackPanelEvent } from "../../../models/Event";
import { FinOpsCostKeys, FinOpsTrackingViewTypeEnum, IFinOpsBudgetAllocationCombined, IFinOpsMonthlyBudgetAllocationRecord, MonthKey } from '../../../models/FinOpsBudgets';
import React, { useMemo, useState } from 'react';
import StickyTable, { IStickyTableCellOption, IStickyTableFooterOption, IStickyTableHeaderOption } from '../../common/table/StickyTable';
import { getCurrentMonth, getFiscalYearRange, getUTCMonthStartDate, getUTCMonthStartTime, getUTCMonthStartTimeByMonthName } from '../../../utils/BudgetUtils';

import FeedbackPanel from "../../feedback/FeedbackPanel";
import { Input } from '@fluentui/react-components';
import { SectionHeader } from '../../common/SectionHeader';
import ServiceEditDialog from './ServiceEditDialog';
import { exportToCsv } from '../../../utils/common';
import html2canvas from "html2canvas";
import moment from 'moment';
import styles from './DetailTable.less';
import { trackEventCallback } from "../../../utils/AppInsights";
import { useBoolean } from "@fluentui/react-hooks";
import useEventBus from "../../../hooks/useEventBus";
import { useFlights } from "../../../hooks/useSettings";
import { useMsal } from "@azure/msal-react";
import { useUpdateMonthlyBudgetAllocation } from '../../../hooks/useFinOpsBudget';

const feedbackIconProps: IIconProps = { iconName: "Feedback" };

export interface IFinOpsCostTableProps {
    data: IFinOpsBudgetAllocationCombined[];
    previousFyData: IFinOpsBudgetAllocationCombined[];
    viewType: FinOpsTrackingViewTypeEnum;
    isloading: boolean;
    IsDrilldownView: boolean;
    currentFiscalYear: string
}

const getCellOptions = (cell: Cell): IStickyTableCellOption => {
    const isMonthColumn = /^\w+ \d{4}$/.test(cell.column.id); // e.g., "June 2025"
    let isCurrentOrFuture = false;

    if (isMonthColumn) {
        const [monthName, year] = cell.column.id.split(' ');
        const columnDate = getUTCMonthStartTimeByMonthName(`${monthName} 1, ${year}`);
        const currentMonthStart = getCurrentMonth();
        isCurrentOrFuture = columnDate >= currentMonthStart;
    }

    return {
        props: {
            key: cell.column.id,
            className: [
                FinOpsCostKeys.find((item) => item === cell.column.id) ? styles.textAlignRight : '',
                isCurrentOrFuture ? styles.blueText : ''
            ].join(' ').trim(),
        }
    };
};

const getHeaderOption = (header: HeaderGroup): IStickyTableHeaderOption => {
    const isMonthColumn = /^\w+ \d{4}$/.test(header.id);
    let isCurrentOrFuture = false;

    if (isMonthColumn) {
        const [monthName, year] = header.id.split(' ');
        const columnDate = getUTCMonthStartTimeByMonthName(`${monthName} 1, ${year}`);
        const currentMonthStart = getCurrentMonth();
        isCurrentOrFuture = columnDate >= currentMonthStart;
    }

    return {
        props: {
            key: header.id,
            className: [
                FinOpsCostKeys.find((item) => item === header.id) ? styles.textAlignRight : '',
                isCurrentOrFuture ? styles.blueText : ''
            ].join(' ').trim(),
        }
    };
};

const getFooterOption = (footer: HeaderGroup): IStickyTableFooterOption => {
    const isMonthColumn = /^\w+ \d{4}$/.test(footer.id); // e.g., "June 2025"
    let isCurrentOrFuture = false;

    if (isMonthColumn) {
        const [monthName, year] = footer.id.split(' ');
        const columnDate = getUTCMonthStartTimeByMonthName(`${monthName} 1, ${year}`);
        const currentMonthStart = getCurrentMonth();
        isCurrentOrFuture = columnDate >= currentMonthStart;
    }

    return {
        props: {
            key: footer.id,
            className: [
                FinOpsCostKeys.find((item) => item === footer.id) ? styles.textAlignRight : '',
                isCurrentOrFuture ? styles.blueText : ''
            ].join(' ').trim(),
        }
    };
};

const intlFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
});

const FinOpsCostTable: React.FC<IFinOpsCostTableProps> = (props) => {
    const [isLoading, setLoading] = useState(props.isloading);
    const [isEditing, setIsEditing] = useState(false);
    const { instance } = useMsal();
    const { data: flights } = useFlights();
    const account = instance.getActiveAccount();
    const [isFeedbackPanelOpen, { setTrue: openFeedbackPanel, setFalse: dismissFeedbackPanel }] = useBoolean(false);
    const [currentURL, setCurrentURL] = useState<string>('');
    const [currentScreenshotURL, setCurrentScreenshotURL] = useState<string>('');
    const [isServiceEditPanelOpen, setIsServiceEditPanelOpen] = useState(false);
    const previousFiscalYear = `FY${parseInt(props.currentFiscalYear.slice(2)) - 1}`;
    const { start: fiscalStart, end: fiscalEnd } = React.useMemo(() => getFiscalYearRange(props.currentFiscalYear), []);
    const { start: prevFiscalStart, end: prevFiscalEnd } = React.useMemo(() => getFiscalYearRange(previousFiscalYear), []);

    const openServiceEditPanel = () => setIsServiceEditPanelOpen(true);
    const dismissServiceEditPanel = () => setIsServiceEditPanelOpen(false);

    const normalizedData = useMemo(() => {
      return props.data.map(item => ({
        ...item,
        cost: item.cost != null ? Math.round(item.cost) : item.cost,
        budget: item.budget != null ? Math.round(item.budget) : item.budget,
      }));
    }, [props.data]);

    const normalizedLastFyData = useMemo(() => {
      return props.previousFyData.map(item => ({
        ...item,
        cost: item.cost != null ? Math.round(item.cost) : item.cost,
        budget: item.budget != null ? Math.round(item.budget) : item.budget,
      }));
    }, [props.previousFyData]);

    const [monthlyBudgets, setMonthlyBudgets] = useState<Record<string, number>>(() => {
        const initial: Record<string, number> = {};
        normalizedData.forEach(item => {
            if (!item.month) return;
            const label = getUTCMonthStartDate(item.month).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
            initial[label] = (initial[label] || 0) + (item.budget ?? 0);
        });
        return initial;
    });

    const initialTotalBudget = useMemo(() => {
      return normalizedData.reduce((sum, item) => sum + (item.budget ?? 0), 0);
    }, [normalizedData]);

    const handleBudgetChange = (month: string, newValue: number) => {
        const otherMonths = Object.entries(monthlyBudgets).filter(([m]) => m !== month);
        const totalOther = otherMonths.reduce((sum, [, val]) => sum + val, 0);

        if (totalOther + monthlyBudgets[month] - newValue < 0) {
            alert("Exceed total budget, please reduce budget of other months first.");
            return;
        }

        setMonthlyBudgets(prev => ({ ...prev, [month]: newValue }));
    };

    const { mutate: updateBudgetAllocations, isLoading: isSaving, isError, error } = useUpdateMonthlyBudgetAllocation(
        () => {
            setIsEditing(false);
            if (window.confirm('Budget allocations updated successfully. Click OK to refresh the page.')) {
                window.location.reload();
            }
        },
        (error) => {
            console.error('Failed to update budget allocations:', error);
            alert('Failed to update budget allocations.');
        }
    );

    const handleSave = () => {
      const newTotal = Object.values(monthlyBudgets).reduce((sum, val) => sum + val, 0);
      if (newTotal !== initialTotalBudget) {
        alert(`Save failed: total budget (${newTotal}) does not match original (${initialTotalBudget}).`);
        return;
      }

      const allocations: IFinOpsMonthlyBudgetAllocationRecord[] = [];
        
        Object.entries(monthlyBudgets).forEach(([monthLabel, budget]) => {
            const monthDate = new Date(monthLabel);
            
            const calendarMonth = monthDate.getMonth(); // 0 = Jan, ..., 11 = Dec
            const monthNumber = ((calendarMonth + 12 - 5) % 12) + 1;

            const existingIndex = allocations.findIndex(a => 
                a.fiscalYear === props.currentFiscalYear &&
                a.serviceId === normalizedData[0]?.serviceId &&
                a.category === normalizedData[0]?.category && 
                a.type === normalizedData[0]?.type
            );

            if (existingIndex >= 0) {
                // Update existing entry
                allocations[existingIndex][`month${monthNumber}` as MonthKey] = budget;
            } else {
                // Create a new entry
                const currentFiscalYear = props.currentFiscalYear;
                const serviceId = normalizedData[0]?.serviceId || '';
                const category = normalizedData[0]?.category || '';
                const type = normalizedData[0]?.type || '';
                
                // Create an allocation with all months initialized to 0
                const allocation: IFinOpsMonthlyBudgetAllocationRecord = {
                version: new Date(),
                fiscalYear: currentFiscalYear,
                serviceId,
                category,
                type,
                submitter: '',
                month1: 0, month2: 0, month3: 0, month4: 0,
                month5: 0, month6: 0, month7: 0, month8: 0,
                month9: 0, month10: 0, month11: 0, month12: 0
                };
                
                // Set the specific month value
                allocation[`month${monthNumber}` as MonthKey] = budget;
            
                allocations.push(allocation);
          }
        });

      updateBudgetAllocations(allocations)
    };

    const pivotedData = useMemo(() => {
        const groupedByMonth: Record<string, IFinOpsBudgetAllocationCombined[]> = {};

        (normalizedData || []).forEach(item => {
            const month = item.month;
            if (!month) return;

            
            const date = getUTCMonthStartDate(month);
            const monthLabel = date.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

            if (!groupedByMonth[monthLabel]) {
                groupedByMonth[monthLabel] = [];
            }
            groupedByMonth[monthLabel].push(item);
        });

        const result: any[] = [
            { metric: 'Cost' },
            { metric: 'Budget' }
        ];

        Object.entries(groupedByMonth).forEach(([monthLabel, items]) => {
            const totalCost = items.reduce((sum, item) => sum + (item.cost ?? 0), 0);

            result[0][monthLabel] = totalCost;
            result[1][monthLabel] = monthlyBudgets[monthLabel] ?? 0;
        });

        return result;
    }, [normalizedData, monthlyBudgets]);

    const pivotedColumns: Column<any>[] = useMemo(() => {
        const monthKeys = [
            ...new Set(
                (normalizedData || [])
                .map(d => d.month)
                .filter((month): month is string => typeof month === 'string')
                .map(month => {
                    const date = getUTCMonthStartDate(month);
                    return date.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
                })
                .filter((label): label is string => label !== null)
            )
        ].sort((a, b) => moment.utc(a, "YYYY-MM").startOf("month").valueOf() - moment.utc(b, "YYYY-MM").startOf("month").valueOf());

        const sortedMonthKeys = monthKeys.sort((a, b) => {
            const dateA = new Date(a);
            const dateB = new Date(b);
            return moment.utc(dateA, "YYYY-MM").startOf("month").valueOf() - moment.utc(dateB, "YYYY-MM").startOf("month").valueOf();
        });

        const cols: Column<any>[] = [
            {
                Header: 'Metric',
                accessor: 'metric',
            },
            ...sortedMonthKeys.map((month) => ({
                Header: month,
                accessor: month,
                Cell: ({ row }: { row: Row<any>; column: Column<any> }) => {
                        const value =  row.original[month];
                        return row.original.metric === 'Budget' ? (
                            isEditing ? (
                            <Input
                                type="number"
                                value={value?.toString() ?? ''}
                                onChange={(e) => handleBudgetChange(month, parseInt(e.target.value))}
                                className={styles.budgetInput}
                                style={{ width: '80%' }}
                                contentBefore={<span>$</span>}
                            />
                            ) : (
                            value != null ? intlFormatter.format(value) : '-'
                            )
                        ) : (
                            value != null ? intlFormatter.format(value) : '-'
                    );
                },
                className: styles.textAlignRight,
            })),
        ];


        return cols;
    }, [normalizedData, isEditing, fiscalStart, fiscalEnd]);

    const pivotedYoYCostData = useMemo(() => {
        const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });

        const rowPrev: { [key: string]: string | number } = { metric: `Cost ${previousFiscalYear}` };
        const rowCurr: { [key: string]: string | number } = { metric: `Cost ${props.currentFiscalYear}` };
        const rowDelta: { [key: string]: string | number } = { metric: 'YoY ∆' };
        const rowPercent: { [key: string]: string | number | null } = { metric: 'YoY %' };

        for (let i = 0; i < 12; i++) {
            let prevMonth = prevFiscalStart;
            let currMonth = fiscalStart;
            currMonth = new Date(Date.UTC(currMonth.getUTCFullYear(), currMonth.getUTCMonth() + i, 1));
            prevMonth = new Date(Date.UTC(prevMonth.getUTCFullYear(), prevMonth.getUTCMonth() + i, 1));

            const label = `${monthFormatter.format(currMonth)}`;

            const prevMonthTime = prevMonth.getTime();
            const currMonthTime = currMonth.getTime();

            const prevCost = normalizedLastFyData.filter(item => item.month && getUTCMonthStartDate(item.month).getTime() === prevMonthTime).reduce((sum, item) => sum + (item.cost ?? 0), 0);
            const currCost = normalizedData.filter(item => item.month && getUTCMonthStartDate(item.month).getTime() === currMonthTime).reduce((sum, item) => sum + (item.cost ?? 0), 0);

            rowPrev[label] = prevCost;
            rowCurr[label] = currCost;
            rowDelta[label] = currCost - prevCost;
            rowPercent[label] = prevCost !== 0 ? (currCost - prevCost) / prevCost : null;
        }

        return [rowPrev, rowCurr, rowDelta, rowPercent];
    }, [normalizedData, normalizedLastFyData, fiscalStart, prevFiscalStart, props.currentFiscalYear, previousFiscalYear]);

    const pivotedYoYCostColumns: Column<any>[] = useMemo(() => {
        const monthKeys = pivotedYoYCostData[0]
            ? Object.keys(pivotedYoYCostData[0]).filter(key => key !== 'metric')
            : [];

        
        const currentMonthStart = getCurrentMonth();

        return [
            { Header: 'Metric', accessor: 'metric' },
            ...monthKeys.map(month => ({
                Header: month,
                accessor: month,
                Cell: ({ row }: { row: Row<any> }) => {
                    const value = row.original[month];
                    
                    const isCurrentYear = row.original.metric === `Cost ${props.currentFiscalYear}`;

                    const monthIndex = monthKeys.indexOf(month);
                    let monthDate = fiscalStart;
                    monthDate = new Date(Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth() + monthIndex, 1));

                    const isCurrentOrFuture = isCurrentYear && monthDate >= currentMonthStart;

                    
                    const className = [
                        styles.textAlignRight,
                        isCurrentOrFuture ? styles.blueText : ''
                    ].join(' ').trim();


                    if (row.original.metric === 'YoY %') {
                        return <span className={className}>{value != null ? `${(value * 100).toFixed(1)}%` : '-'}</span>;
                    }
                    return <span className={className}>{value != null ? intlFormatter.format(value) : '-'}</span>;
                },
                className: styles.textAlignRight,
            }))
        ];
    }, [pivotedYoYCostData, fiscalStart, props.currentFiscalYear]);

    const yoyTableInstance = useTable(
        {
            columns: pivotedYoYCostColumns,
            data: pivotedYoYCostData,
        },
        useSortBy
    ) as TableInstance<any>;

    const handleDownload = () => {
        exportToCsv(pivotedData, 'FinOps Budget Tracking (Pivoted)', undefined, undefined, (key: string, value: string | number) => {
            if (value === undefined || value === null) return '-';
            return String(value);
        });
    };

    const handleDownloadYoY = () => {
        exportToCsv(pivotedYoYCostData, 'FinOps Budget Tracking (PivotedYoY)', undefined, undefined, (key: string, value: string | number) => {
            if (value === undefined || value === null) return '-';
            return String(value);
        });
    };

    const tableInstance = useTable(
        {
            columns: pivotedColumns,
            data: pivotedData,
        },
        useSortBy 
    ) as TableInstance<any>;

    const captureScreenshot = async () => {
        trackEventCallback(LogComponent.BudgetTracking, LogElement.BudgetTrackingServiceLayout, "Send Feedback Chart", LogTarget.Button, undefined, EventType.Click);

        const url = window.location.href;
        setCurrentURL(url);

        await openFeedbackPanel();

        const targetElement = document.getElementById(`FeedbackSection_BudgetTrackingTable`);

        if (targetElement) {
            setTimeout(() => {
                html2canvas(targetElement).then((canvas) => {
                    const screenshotDataUrl = canvas.toDataURL('image/png');
                    setCurrentScreenshotURL(screenshotDataUrl);
                });
            }, 100);
        }

    };

    const handleDismissFeedbackPanel = () => {
        setCurrentScreenshotURL('');
        dismissFeedbackPanel();
    };

    useEventBus<OpenFeedbackPanelEvent>(EventTypeEnum.OpenFeedbackPanel, openFeedbackPanel, []);

    return (
        <div className={styles.tableContainer} id={`FeedbackSection_BudgetTrackingTable`}>
            {flights?.enableUserFeedback &&
                <IconButton
                    title="Send feedback"
                    className={styles.feedbackIcon}
                    iconProps={feedbackIconProps}
                    onClick={captureScreenshot}
                />
            }
            <div className={styles.costTableView}>
                <SectionHeader
                    className={styles.sectionHeader}
                    title="Monthly Budget Overview"
                    extra={
                        <>
                            {props.IsDrilldownView ? <ActionButton
                                text={isEditing ? "Save" : "Edit"}
                                iconProps={{ iconName: isEditing ? 'CheckMark' : 'Edit' }}
                                onClick={() => {  
                                    if (isEditing) {
                                        handleSave();
                                    } else {
                                        setIsEditing(true);
                                    }
                                }
                            }
                            /> : 
                            <ActionButton
                                text={isEditing ? "Save" : "Edit"}
                                iconProps={{ iconName: isEditing ? 'CheckMark' : 'Edit' }}
                                onClick={() => openServiceEditPanel()}
                            />
                            }
                            {props.IsDrilldownView && isEditing && <ActionButton
                                text={"Cancel"}
                                iconProps={{ iconName: 'Clear', style: { fontSize: 'small' } }}
                                onClick={() => setIsEditing(false)}
                            />}
                            <ActionButton
                                text="Download"
                                iconProps={{ iconName: 'Download' }}
                                onClick={handleDownload}
                            />
                        </>
                    }
                />
                <StickyTable
                    styles={{
                        container: styles.tableContainer,
                        bodyContainer: styles.tableBodyContainer,
                        footerContainer: styles.tableSectionContainer,
                        headerContainer: styles.tableSectionContainer,
                        stickyFooterContainer: styles.tableStickyFooterContainer,
                    }}
                    key={props.viewType}
                    cellOption={getCellOptions}
                    headerOption={getHeaderOption}
                    footerOption={getFooterOption}
                    loading={isLoading}
                    loadMore={false}
                    emptyText="The selected services don't have data"
                    table={tableInstance}
                />
            </div>
            <div className={styles.costTableView}>
                <SectionHeader
                    className={styles.sectionHeader}
                    title={`Year-over-Year Cost Comparison (${previousFiscalYear} vs ${props.currentFiscalYear})`}
                    extra={
                        <>
                            <ActionButton
                                text="Download"
                                iconProps={{ iconName: 'Download' }}
                                onClick={handleDownloadYoY}
                            />
                        </>
                    }
                />
                <StickyTable
                    styles={{
                        container: styles.tableContainer,
                        bodyContainer: styles.tableBodyContainer,
                        footerContainer: styles.tableSectionContainer,
                        headerContainer: styles.tableSectionContainer,
                        stickyFooterContainer: styles.tableStickyFooterContainer,
                    }}
                    key={`yoy-${props.viewType}`}
                    cellOption={getCellOptions}
                    headerOption={getHeaderOption}
                    footerOption={getFooterOption}
                    loading={isLoading}
                    loadMore={false}
                    emptyText="No YoY data available"
                    table={yoyTableInstance}
                />
            </div>
            <FeedbackPanel
                isPanelOpen={isFeedbackPanelOpen}
                onDismiss={handleDismissFeedbackPanel}
                userName={account?.username}
                currentURL={currentURL}
                screenshotInputURL={currentScreenshotURL}
                FeedbackTab={LogComponent.BudgetTracking}
            />
            <ServiceEditDialog
                open={isServiceEditPanelOpen}
                onClose={dismissServiceEditPanel}
                data={props.data}
                fiscalYearStart={fiscalStart}
                fiscalYearEnd={fiscalEnd} 
                currentFiscalYear={props.currentFiscalYear}
            />
    </div>
    );
};

export default FinOpsCostTable;
